<template>
  <Content class="food-sales-report">
    <Card>
      <div slot="title">商品销售报表</div>

      <Tabs v-model="activeTab" :animated="false" @on-click="onTabChange">
        <TabPane label="商品销售" name="sales" />
        <TabPane label="营业额汇总" name="revenue" />
        <TabPane label="退款流水" name="refund" />
      </Tabs>

      <p class="hint">{{ hintText }}</p>

      <Form :model="form" inline :label-width="72" class="search-form">
        <FormItem label="日期">
          <DatePicker
            v-model="form.dateRange"
            type="daterange"
            placement="bottom-start"
            placeholder="选择日期范围"
            style="width: 220px;"
            :options="dateOptions"
          />
        </FormItem>
        <FormItem label="快捷">
          <ButtonGroup>
            <Button :type="quick === 'today' ? 'primary' : 'default'" @click="setQuick('today')">今天</Button>
            <Button :type="quick === '7d' ? 'primary' : 'default'" @click="setQuick('7d')">近7天</Button>
            <Button :type="quick === '30d' ? 'primary' : 'default'" @click="setQuick('30d')">近30天</Button>
          </ButtonGroup>
        </FormItem>
        <template v-if="activeTab === 'sales'">
          <FormItem label="履约">
            <Select v-model="form.fulfillment_mode" clearable placeholder="全部" style="width: 120px;">
              <Option value="dine_in">堂食</Option>
              <Option value="pack">打包</Option>
            </Select>
          </FormItem>
          <FormItem label="汇总">
            <Select v-model="form.group_by" style="width: 140px;">
              <Option value="sku">商品+规格</Option>
              <Option value="food">仅商品</Option>
            </Select>
          </FormItem>
          <FormItem label="排序">
            <Select v-model="form.sort" style="width: 120px;">
              <Option value="qty">按销量</Option>
              <Option value="amount">按金额</Option>
            </Select>
          </FormItem>
        </template>
        <FormItem :label-width="12">
          <Button type="primary" :loading="loading" @click="search">查询</Button>
          <Button style="margin-left: 8px;" @click="reset">重置</Button>
          <Button
            type="info"
            style="margin-left: 8px;"
            :disabled="!exportRows.length"
            @click="exportCsv"
          >
            导出 CSV
          </Button>
        </FormItem>
      </Form>

      <Alert v-if="activeTab === 'revenue' && revenueSummary" type="success" show-icon class="summary-alert">
        区间合计：净营业额
        <b>￥{{ Number(revenueSummary.net_yuan || 0).toFixed(2) }}</b>
        （实付 ￥{{ Number(revenueSummary.gross_yuan || 0).toFixed(2) }}
        − 已退 ￥{{ Number(revenueSummary.refunded_yuan || 0).toFixed(2) }}）
        ，订单 {{ revenueSummary.order_count || 0 }} 笔
      </Alert>

      <Alert v-if="activeTab === 'refund' && refundSummary" type="warning" show-icon class="summary-alert">
        区间退款合计
        <b>￥{{ Number(refundSummary.refund_yuan || 0).toFixed(2) }}</b>
        （微信 ￥{{ Number(refundSummary.wx_refund_yuan || 0).toFixed(2) }}
        + 余额 ￥{{ Number(refundSummary.balance_refund_yuan || 0).toFixed(2) }}）
        ，共 {{ refundSummary.refund_count || 0 }} 笔
      </Alert>

      <Table
        :columns="activeColumns"
        :data="activeRows"
        :loading="loading"
        :height="tablesHeight"
        border
        stripe
      />

      <div v-if="activeTab === 'refund'" class="pager">
        <Page
          :total="refundTotal"
          :current="refundPage"
          :page-size="refundPageSize"
          show-total
          show-elevator
          @on-change="onRefundPageChange"
        />
      </div>
    </Card>
  </Content>
</template>

<script>
import {
  getFoodSalesReport,
  getFoodRevenueSummary,
  getFoodRefundLedger,
} from '@/api/home'

function pad(n) {
  return n < 10 ? `0${n}` : String(n)
}

function ymd(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function moneyRender(h, val) {
  return h('span', Number(val || 0).toFixed(2))
}

const SOURCE_LABEL = {
  admin: '管理端退款',
  merchant: '店员退款',
  cancel: '取消退款',
  refund: '退款',
  legacy: '历史退款',
}

export default {
  name: 'FoodSalesReport',
  data() {
    const today = startOfDay(new Date())
    return {
      activeTab: 'sales',
      loading: false,
      quick: 'today',
      form: {
        dateRange: [today, today],
        fulfillment_mode: '',
        group_by: 'sku',
        sort: 'qty',
      },
      dateOptions: {
        disabledDate(date) {
          return date && date.valueOf() > Date.now()
        },
      },
      salesRows: [],
      revenueRows: [],
      revenueSummary: null,
      refundRows: [],
      refundSummary: null,
      refundTotal: 0,
      refundPage: 1,
      refundPageSize: 50,
      tablesHeight: 420,
      salesColumns: [
        { title: '排名', key: 'rank', width: 80, align: 'center' },
        { title: '商品', key: 'food_name', minWidth: 180, align: 'center' },
        {
          title: '规格',
          key: 'spec_label',
          minWidth: 100,
          align: 'center',
          render: (h, { row }) => h('span', row.spec_label || '—'),
        },
        { title: '销量(件)', key: 'qty', minWidth: 100, align: 'center' },
        {
          title: '销售额(元)',
          key: 'amount_yuan',
          minWidth: 100,
          align: 'center',
          render: (h, { row }) => moneyRender(h, row.amount_yuan),
        },
        { title: '订单数', key: 'order_count', minWidth: 100, align: 'center' },
        {
          title: '销量占比',
          key: 'qty_share',
          minWidth: 100,
          align: 'center',
          render: (h, { row }) =>
            h('span', `${((Number(row.qty_share) || 0) * 100).toFixed(1)}%`),
        },
        {
          title: '金额占比',
          key: 'amount_share',
          minWidth: 100,
          align: 'center',
          render: (h, { row }) =>
            h('span', `${((Number(row.amount_share) || 0) * 100).toFixed(1)}%`),
        },
      ],
      revenueColumns: [
        { title: '日期', key: 'date', minWidth: 120, align: 'center' },
        { title: '订单数', key: 'order_count', minWidth: 90, align: 'center' },
        {
          title: '实付(元)',
          key: 'gross_yuan',
          minWidth: 110,
          align: 'center',
          render: (h, { row }) => moneyRender(h, row.gross_yuan),
        },
        {
          title: '已退(元)',
          key: 'refunded_yuan',
          minWidth: 110,
          align: 'center',
          render: (h, { row }) =>
            h(
              'span',
              { style: Number(row.refunded_yuan) > 0 ? { color: '#ed4014' } : null },
              Number(row.refunded_yuan || 0).toFixed(2)
            ),
        },
        {
          title: '净营业额(元)',
          key: 'net_yuan',
          minWidth: 120,
          align: 'center',
          render: (h, { row }) =>
            h('span', { style: { fontWeight: 600 } }, Number(row.net_yuan || 0).toFixed(2)),
        },
      ],
      refundColumns: [
        { title: '退款时间', key: 'refund_at', minWidth: 160, align: 'center' },
        {
          title: '订单号',
          key: 'order_id',
          minWidth: 180,
          align: 'center',
          render: (h, { row }) => h('span', row.order_id || '—'),
        },
        {
          title: '支付单号',
          key: 'out_trade_no',
          minWidth: 160,
          align: 'center',
          render: (h, { row }) => h('span', row.out_trade_no || '—'),
        },
        {
          title: '订单状态',
          key: 'status_label',
          minWidth: 100,
          align: 'center',
        },
        {
          title: '类型',
          key: 'source',
          minWidth: 100,
          align: 'center',
          render: (h, { row }) =>
            h('span', SOURCE_LABEL[row.source] || row.source || '—'),
        },
        {
          title: '退款(元)',
          key: 'refund_yuan',
          minWidth: 100,
          align: 'center',
          render: (h, { row }) =>
            h('span', { style: { color: '#ed4014' } }, Number(row.refund_yuan || 0).toFixed(2)),
        },
        {
          title: '微信(元)',
          key: 'wx_refund_yuan',
          minWidth: 90,
          align: 'center',
          render: (h, { row }) => moneyRender(h, row.wx_refund_yuan),
        },
        {
          title: '余额(元)',
          key: 'balance_refund_yuan',
          minWidth: 90,
          align: 'center',
          render: (h, { row }) => moneyRender(h, row.balance_refund_yuan),
        },
        {
          title: '退款结果',
          key: 'refund_result_status',
          minWidth: 110,
          align: 'center',
          render: (h, { row }) => {
            const map = {
              processing: '退款处理中',
              success: '退款成功',
              closed: '退款关闭',
              abnormal: '退款异常',
            }
            const s = String(row.refund_result_status || '').toLowerCase()
            const text = map[s] || (s ? s : '—')
            const color =
              s === 'success'
                ? 'success'
                : s === 'processing'
                  ? 'warning'
                  : s === 'closed' || s === 'abnormal'
                    ? 'error'
                    : 'default'
            return h('Tag', { props: { color } }, text)
          },
        },
        {
          title: '说明',
          key: 'note',
          minWidth: 160,
          align: 'center',
          render: (h, { row }) => h('span', row.note || '—'),
        },
      ],
    }
  },
  computed: {
    hintText() {
      if (this.activeTab === 'revenue') {
        return '按支付日汇总：净营业额 = 实付（微信+余额）− 该日订单累计已退。用于历史对账。'
      }
      if (this.activeTab === 'refund') {
        return '按退款发生时间列出退款流水。微信退款结果：处理中 → 成功/关闭/异常（以回调为准）。'
      }
      return '按已支付订单汇总商品销量与销售额（行原价 = 单价 × 数量）。'
    },
    activeColumns() {
      if (this.activeTab === 'revenue') return this.revenueColumns
      if (this.activeTab === 'refund') return this.refundColumns
      return this.salesColumns
    },
    activeRows() {
      if (this.activeTab === 'revenue') return this.revenueRows
      if (this.activeTab === 'refund') return this.refundRows
      return this.salesRows
    },
    exportRows() {
      return this.activeRows
    },
  },
  mounted() {
    this.setTableHeight()
    window.addEventListener('resize', this.setTableHeight)
    this.fetchData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setTableHeight)
  },
  methods: {
    setTableHeight() {
      try {
        const aH = window.innerHeight - 360
        this.tablesHeight = aH > 240 ? aH : 240
      } catch (e) {
        this.tablesHeight = 420
      }
    },
    onTabChange() {
      this.refundPage = 1
      this.fetchData()
    },
    setQuick(wh) {
      const end = startOfDay(new Date())
      let start = end
      if (wh === '7d') {
        start = new Date(end.getTime() - 6 * 24 * 60 * 60 * 1000)
      } else if (wh === '30d') {
        start = new Date(end.getTime() - 29 * 24 * 60 * 60 * 1000)
      }
      this.quick = wh
      this.form.dateRange = [start, end]
      this.refundPage = 1
      this.fetchData()
    },
    search() {
      this.quick = ''
      this.refundPage = 1
      this.fetchData()
    },
    reset() {
      const today = startOfDay(new Date())
      this.quick = 'today'
      this.form = {
        dateRange: [today, today],
        fulfillment_mode: '',
        group_by: 'sku',
        sort: 'qty',
      }
      this.refundPage = 1
      this.fetchData()
    },
    onRefundPageChange(p) {
      this.refundPage = p
      this.fetchData()
    },
    rangeParams() {
      const range = this.form.dateRange
      let from = ''
      let to = ''
      if (Array.isArray(range) && range[0] && range[1]) {
        from = ymd(new Date(range[0]))
        to = ymd(new Date(range[1]))
      } else {
        const today = ymd(new Date())
        from = today
        to = today
      }
      return { date_from: from, date_to: to }
    },
    unwrap(res) {
      const body = res && res.data
      const ok = body && (body.success === true || body.code === 200)
      const data = (body && body.data) || {}
      if (!ok) {
        throw new Error((body && (body.message || body.msg)) || '加载失败')
      }
      return data
    },
    async fetchData() {
      this.loading = true
      try {
        const { date_from, date_to } = this.rangeParams()
        if (this.activeTab === 'revenue') {
          const data = this.unwrap(
            await getFoodRevenueSummary({ date_from, date_to })
          )
          this.revenueRows = Array.isArray(data.days) ? data.days : []
          this.revenueSummary = data.summary || null
        } else if (this.activeTab === 'refund') {
          const data = this.unwrap(
            await getFoodRefundLedger({
              date_from,
              date_to,
              page: this.refundPage,
              pageSize: this.refundPageSize,
            })
          )
          this.refundRows = Array.isArray(data.rows) ? data.rows : []
          this.refundSummary = data.summary || null
          this.refundTotal = Number(data.total) || 0
        } else {
          const data = this.unwrap(
            await getFoodSalesReport({
              date_from,
              date_to,
              fulfillment_mode: this.form.fulfillment_mode || undefined,
              group_by: this.form.group_by,
              sort: this.form.sort,
              limit: 200,
            })
          )
          this.salesRows = Array.isArray(data.rows) ? data.rows : []
        }
      } catch (e) {
        this.$Message.error((e && e.message) || '加载报表失败')
        if (this.activeTab === 'revenue') {
          this.revenueRows = []
          this.revenueSummary = null
        } else if (this.activeTab === 'refund') {
          this.refundRows = []
          this.refundSummary = null
          this.refundTotal = 0
        } else {
          this.salesRows = []
        }
      } finally {
        this.loading = false
      }
    },
    exportCsv() {
      const rows = this.exportRows
      if (!rows.length) return
      const { date_from, date_to } = this.rangeParams()
      let headers = []
      let lines = []
      let filename = ''

      if (this.activeTab === 'revenue') {
        headers = ['日期', '订单数', '实付', '已退', '净营业额']
        lines = [headers.join(',')]
        for (const r of rows) {
          lines.push(
            [
              r.date,
              r.order_count,
              Number(r.gross_yuan || 0).toFixed(2),
              Number(r.refunded_yuan || 0).toFixed(2),
              Number(r.net_yuan || 0).toFixed(2),
            ].join(',')
          )
        }
        filename = `营业额汇总_${date_from}_${date_to}.csv`
      } else if (this.activeTab === 'refund') {
        headers = [
          '退款时间',
          '订单号',
          '支付单号',
          '订单状态',
          '类型',
          '退款金额',
          '微信',
          '余额',
          '退款结果',
          '说明',
        ]
        lines = [headers.join(',')]
        const resultMap = {
          processing: '退款处理中',
          success: '退款成功',
          closed: '退款关闭',
          abnormal: '退款异常',
        }
        for (const r of rows) {
          const rs = String(r.refund_result_status || '').toLowerCase()
          lines.push(
            [
              `"${String(r.refund_at || '').replace(/"/g, '""')}"`,
              r.order_id || '',
              `"${String(r.out_trade_no || '').replace(/"/g, '""')}"`,
              r.status_label || '',
              SOURCE_LABEL[r.source] || r.source || '',
              Number(r.refund_yuan || 0).toFixed(2),
              Number(r.wx_refund_yuan || 0).toFixed(2),
              Number(r.balance_refund_yuan || 0).toFixed(2),
              resultMap[rs] || rs || '—',
              `"${String(r.note || '').replace(/"/g, '""')}"`,
            ].join(',')
          )
        }
        filename = `退款流水_${date_from}_${date_to}.csv`
      } else {
        headers = [
          '排名',
          '商品',
          '规格',
          '销量',
          '销售额',
          '订单数',
          '销量占比',
          '金额占比',
        ]
        lines = [headers.join(',')]
        for (const r of rows) {
          lines.push(
            [
              r.rank,
              `"${String(r.food_name || '').replace(/"/g, '""')}"`,
              `"${String(r.spec_label || '').replace(/"/g, '""')}"`,
              r.qty,
              Number(r.amount_yuan || 0).toFixed(2),
              r.order_count,
              `${((Number(r.qty_share) || 0) * 100).toFixed(1)}%`,
              `${((Number(r.amount_share) || 0) * 100).toFixed(1)}%`,
            ].join(',')
          )
        }
        filename = `商品销售报表_${date_from}_${date_to}.csv`
      }

      const blob = new Blob(['\ufeff' + lines.join('\n')], {
        type: 'text/csv;charset=utf-8;',
      })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()
      URL.revokeObjectURL(link.href)
    },
  },
}
</script>

<style scoped>
.food-sales-report .hint {
  margin: 0 0 12px;
  color: #808695;
  font-size: 13px;
}
.search-form {
  margin-bottom: 12px;
}
.summary-alert {
  margin-bottom: 12px;
}
.pager {
  margin-top: 12px;
  text-align: right;
}
</style>
